import { createAuthClient } from "better-auth/react"
import { adminClient } from "better-auth/client/plugins"
import { telegramClient } from "better-auth-telegram/client"
import { useQuery } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import type { ReactNode } from "react"
import { Navigate, useLocation } from "react-router"

export const authClient = createAuthClient({
  baseURL: window.location.origin,
  plugins: [adminClient(), telegramClient()],
})

type SetupStatus = { needsSetup: boolean }

export function useSetupStatus() {
  return useQuery({
    queryKey: ["setup-status"],
    queryFn: async () => {
      const res = await fetch("/api/setup-status")
      return res.json() as Promise<SetupStatus>
    },
    staleTime: 10_000,
  })
}

export type Me =
  | { kind: "user" }
  | { kind: "staff"; telegramName: string; salesPointIds: string[]; expiresAt: string | null }

/** Сессия участника Telegram-чата (cookie fds_staff). null — не вошёл. Без редиректа на /login при 401. */
export function useStaffMe(enabled = true) {
  return useQuery({
    queryKey: ["me"],
    enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<Extract<Me, { kind: "staff" }> | null> => {
      const res = await fetch("/api/admin/me", { credentials: "include" })
      if (!res.ok) return null
      const me = (await res.json()) as Me
      return me.kind === "staff" ? me : null
    },
  })
}

export function useIsStaff(): boolean {
  const session = authClient.useSession()
  const staff = useStaffMe(!session.isPending && !session.data?.user)
  return !session.data?.user && !!staff.data
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const session = authClient.useSession()
  const staff = useStaffMe(!session.isPending && !session.data?.user)
  const setupStatus = useSetupStatus()
  const location = useLocation()

  if (session.isPending || setupStatus.isLoading || staff.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (session.data?.user || staff.data) {
    return <>{children}</>
  }

  if (setupStatus.data?.needsSetup) {
    return <Navigate to="/setup" replace />
  }

  const next = encodeURIComponent(location.pathname + location.search)
  return <Navigate to={`/login?next=${next}`} replace />
}
